import { usePtoUnpaidHours } from 'hooks/feature/time-off/usePtoUnpaidHours/usePtoUnpaidHours';
import { toNumber } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import TimeOffType, { CustomTimeOffType } from 'types/enum/TimeOffType';
import { ITimeOffCreateInput } from 'types/TimeOff';
import { getDayRanges, getNowIsoAtUtcWithLocalTimeZone } from 'utils/dateUtils';
import { uuid } from 'utils/uuidUtils';
import { ITimeOffFormData } from '../CreateTimeOffForm/CreateTimeOffForm';

export function useConvertTimeOffFormDataToTimeOffCreateInput() {
  const createTimeOffCreateInput = useCreateTimeOffCreateInput();

  return useCallback(
    (
      { startDate, endDate, members, description, total, paid, type }: ITimeOffFormData,
      activeMemberId: string
    ): ITimeOffCreateInput[] => {
      const dates = !endDate ? [startDate.value!] : getDayRanges(startDate.value!, endDate!.value!);
      return createTimeOffCreateInput(
        dates,
        members,
        description ? description : null,
        total ? total : null,
        paid,
        toNumber(type!),
        activeMemberId
      );
    },
    [createTimeOffCreateInput]
  );
}

export function useCreateTimeOffCreateInput() {
  const enablePtoUnpaidHours = usePtoUnpaidHours();
  const createTimeOffCreateInputDayTransform = useCreateTimeOffCreateInputDayTransform();

  return useCallback(
    (
      dates: Date[],
      memberIds: string[],
      description: string | null,
      totals: { [key: string]: number } | null,
      paid: boolean,
      type: TimeOffType,
      activeMemberId: string
    ): ITimeOffCreateInput[] => {
      const enablePtoTimeTotals = enablePtoUnpaidHours || paid;

      return createTimeOffCreateInputDayTransform(
        dates,
        memberIds,
        description,
        (day) => (totals && enablePtoTimeTotals ? totals[day.toDateString()] : null),
        paid,
        toNumber(type!),
        activeMemberId
      );
    },
    [enablePtoUnpaidHours, createTimeOffCreateInputDayTransform]
  );
}

export function useCreateTimeOffCreateInputDayTransform() {
  const enablePtoUnpaidHours = usePtoUnpaidHours();

  return useCallback(
    (
      dates: Date[],
      memberIds: string[],
      description: string | null,
      getTotal: (date: Date) => number | null,
      paid: boolean,
      type: TimeOffType,
      activeMemberId: string
    ) => {
      const createdOnInDst = DateTime.local().isInDST;
      const enablePtoTimeTotals = enablePtoUnpaidHours || paid;

      return dates.flatMap((day) => {
        const seconds = enablePtoTimeTotals ? getTotal(day) : null;

        return memberIds.map((memberId) => {
          const entry: ITimeOffCreateInput = {
            id: uuid(),
            memberId,
            submitterMemberId: activeMemberId,
            dateStamp: DateTime.fromJSDate(day)
              .startOf('day')
              .toISO({ suppressMilliseconds: true, includeOffset: false }),
            type,
            description,
            paid,
            seconds,
            createdOnDaylightSavingTime: createdOnInDst,
            createdOn: getNowIsoAtUtcWithLocalTimeZone(),
          };

          return entry;
        });
      });
    },
    [enablePtoUnpaidHours]
  );
}

export function useCreateTimeOffCreateInputNumberTotal() {
  const createTimeOffCreateInputDayTransform = useCreateTimeOffCreateInputDayTransform();
  const enablePtoUnpaidHours = usePtoUnpaidHours();

  return useCallback(
    (
      dates: Date[],
      memberIds: string[],
      description: string | null,
      total: number | null,
      paid: boolean,
      type: TimeOffType | CustomTimeOffType,
      activeMemberId: string
    ) => {
      const enablePtoTimeTotals = enablePtoUnpaidHours || paid;

      return createTimeOffCreateInputDayTransform(
        dates,
        memberIds,
        description,
        () => (enablePtoTimeTotals ? total : null),
        paid,
        toNumber(type!),
        activeMemberId
      );
    },
    [enablePtoUnpaidHours, createTimeOffCreateInputDayTransform]
  );
}
